// src/components/WeChatPay.jsx

import React, { useState } from 'react';
import axios from 'axios';
import CryptoJS from 'crypto-js';

/**
 * WeChatPay 组件
 * 英文：WeChatPay Component
 */
const WeChatPay = () => {
  // ========== 1. 定义 State / State Definitions ==========
  const [productTitle, setProductTitle] = useState("自定义网页开发");
  const [merchantTradeNo, setMerchantTradeNo] = useState(`ORDER_${Date.now()}`);
  const [totalAmount, setTotalAmount] = useState("0");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [cashierUrl, setCashierUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // ========== 2. 商户基础信息（示例）/ Merchant Basic Info (Example) ==========
  const merchantId = "75082"; 
  const authenticationCode = "lkjh678jGQDJokl9XQHaHIyfuytttqqq";

  // ========== 3. 辅助函数：生成 GMT 时区时间戳 / Helper: Generate GMT Timestamp ==========
  const getGmtTimestamp = () => {
    const now = new Date();
    const gmtString = new Date(now.getTime() + now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 19)
      .replace("T", " ");
    return gmtString;
  };

  // ========== 4. 辅助函数：URL 编码 / Helper: URL Encoding ==========
  const urlEncode = (str) => {
    return encodeURIComponent(str);
  };

  // ========== 5. 辅助函数：生成 MD5 Token / Helper: Generate MD5 Token ==========
  const generateToken = ({ merchant_id, authentication_code, merchant_trade_no, total_amount }) => {
    const rawString = `merchant_id=${merchant_id}&authentication_code=${authentication_code}&merchant_trade_no=${merchant_trade_no}&total_amount=${total_amount}`;
    const md5Hash = CryptoJS.MD5(rawString).toString();
    return md5Hash;
  };

  // ========== 6. 提交支付请求 / Submit Payment Request ==========
  const handlePaySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    setQrCodeUrl("");
    setCashierUrl("");

    try {
      const createTime = getGmtTimestamp(); // 例如 "2025-06-06 10:23:45"
      const token = generateToken({
        merchant_id: merchantId,
        authentication_code: authenticationCode,
        merchant_trade_no: merchantTradeNo,
        total_amount: totalAmount,
      });

      const params = {
        merchant_id: merchantId,
        authentication_code: authenticationCode,
        product_title: productTitle,
        merchant_trade_no: merchantTradeNo,
        currency: "AUD",
        total_amount: totalAmount,
        create_time: createTime,
        notification_url: "https://yourdomain.com/notification",
        //  notification_url: none,

        token: token,
        // 可选字段 / Optional fields
        // product_desc: "Some product description",
        // return_url: "https://yourdomain.com/return",
        // return_target: "WX",
        // redirect_url: "https://yourdomain.com/cancel",
      };

      const queryString = Object.entries(params)
        .map(([key, value]) => `${key}=${urlEncode(value)}`)
        .join("&");

      const requestUrl = `https://api.superpayglobal.com/payment/wxpayproxy/merchant_request?${queryString}`;
      const response = await axios.get(requestUrl);
      const data = response.data;

      if (data.result === "SUCCESS") {
        if (data.supayCashierURL) {
          setCashierUrl(data.supayCashierURL);
        } else if (data.QRCodeImgPath) {
          setQrCodeUrl(data.QRCodeImgPath);
        } else if (data.QRCodeURL) {
          setQrCodeUrl(data.QRCodeURL);
        }
      } else {
        setErrorMsg(`支付请求失败: ${data.reason}`);
      }
    } catch (err) {
      console.error(err);
      setErrorMsg("请求过程中发生错误，请稍后再试。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-md mx-auto bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-4 text-center">
        微信支付页面
      </h2>

      <form onSubmit={handlePaySubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            商品标题 (product_title):
            <span className="text-gray-500 text-xs ml-1">Merchant Product Title</span>
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-indigo-200"
            value={productTitle}
            onChange={(e) => setProductTitle(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            交易单号 (merchant_trade_no):
            <span className="text-gray-500 text-xs ml-1">Unique Order Number</span>
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-indigo-200"
            value={merchantTradeNo}
            onChange={(e) => setMerchantTradeNo(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            请输入交易金额 (total_amount):
            <span className="text-gray-500 text-xs ml-1">Amount (AUD)</span>
          </label>
          <input
            type="number"
            step="0.01"
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-indigo-200"
            value={totalAmount}
            onChange={(e) => setTotalAmount(e.target.value)}
          />
        </div>

        <div className="text-center">
          <button
            type="submit"
            disabled={loading}
            className={`px-6 py-2 font-medium rounded ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700 text-white"
            }`}
          >
            {loading ? "支付中..." : "发起支付 / Pay Now"}
          </button>
        </div>

        {errorMsg && (
          <p className="text-sm text-red-600 text-center">{errorMsg}</p>
        )}
      </form>

      <div className="mt-6 text-center">
        {cashierUrl && (
          <div className="space-y-2">
            <p className="text-green-700">
              支付收银台链接已生成，点击下方按钮跳转 / Cashier URL generated, click below to proceed
            </p>
            <a
              href={cashierUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-5 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              前往收银台 / Go to Cashier
            </a>
          </div>
        )}

        {qrCodeUrl && (
          <div className="mt-4">
            <p className="text-gray-700 mb-2">
              请使用微信扫码支付 / Please scan the QR code with WeChat
            </p>
            <img
              src={qrCodeUrl}
              alt="WeChat Pay QR Code"
              className="mx-auto w-48 h-48 object-contain"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default WeChatPay;
